import type { TFunction } from 'i18next';
import type { ApprovalAttributes } from '@/types/approval';
import {
  formatSerpText,
  SERP_NOT_LOADED_MESSAGE,
  type SerpDocumentRef,
} from '@/utils/approval/serpDocument';

/** SERP 시즌마감 조정 — 레거시 appr_edit_forms_serp_season_end */
export const SERP_SEASON_END_TYPE = 174;

/** 매장별 정산내역은 같은 문서 번호를 이 문서 종류로 조회(레거시 endDetail 이 EM203 고정) */
export const SERP_SEASON_END_DETAIL_TYPE_CODE = 'EM203';

export const SERP_SEASON_END_LOADING_MESSAGE =
  '데이터를 불러오는 중입니다. 잠시 후 다시 시도해주세요.';

export const isSerpSeasonEndType = (apvType: number): boolean =>
  apvType === SERP_SEASON_END_TYPE;

export type SerpSeasonEndSeasonRow = {
  SSN_NM?: unknown;
  STK_TOT_AMT?: unknown;
  STCKT_TOT_AMT?: unknown;
  DIFF_TOT_AMT?: unknown;
};

export type SerpSeasonEndShopRow = {
  SHOP_CD?: unknown;
  SHOP_ABBR_NM?: unknown;
  GI_RAT?: unknown;
  SSN_NM?: unknown;
  STK_TOT_AMT?: unknown;
  STCKT_TOT_AMT?: unknown;
  ADJ_TOT_AMT?: unknown;
  DIFF_TOT_AMT?: unknown;
  LOSS_TOT_AMT?: unknown;
  STMT_TOT_AMT?: unknown;
  APRV_RMK?: unknown;
};

/** 시즌마감 본문 EAI 응답 원문 */
export type SerpSeasonEndSource = {
  BRAND_EN_NM?: unknown;
  DL_SSN_CD?: unknown;
  INSTR_DE?: unknown;
  SSN_LIST?: unknown;
  [key: string]: unknown;
};

/** 매장별 정산내역 EAI 응답 원문 */
export type SerpSeasonEndDetail = {
  SHOP_LIST?: unknown;
  [key: string]: unknown;
};

export type SerpSeasonEndDraftSource<T> = {
  code: number;
  attributes: SerpDocumentRef;
  display: { source?: T | null };
};

export type SerpSeasonEndState = SerpDocumentRef & {
  source: SerpSeasonEndSource;
  /** 정산내역 조회 결과. 실패도 저장은 허용(레거시 동일) — 그때 null */
  detail: SerpSeasonEndDetail | null;
  /** 정산내역 조회가 끝났는지(성공·실패 모두). 조회 중 저장은 막음 */
  detailSettled: boolean;
  /** SERP 응답으로 자동 입력한 제목 — 이탈 경고는 이 값과 비교 */
  autoSubject: string;
};

const asRows = <T>(raw: unknown): T[] => (Array.isArray(raw) ? raw : []);

export const getSerpSeasonEndSeasonRows = (source: SerpSeasonEndSource) =>
  asRows<SerpSeasonEndSeasonRow>(source.SSN_LIST);

export type SerpSeasonEndShopGroup = {
  shopName: string;
  rows: SerpSeasonEndShopRow[];
};

/** 매장코드별로 묶음 — 레거시처럼 일반 객체 키 순서(숫자형 코드가 먼저, 오름차순) */
export const groupSerpSeasonEndShops = (
  detail: SerpSeasonEndDetail | null,
): SerpSeasonEndShopGroup[] => {
  const grouped = asRows<SerpSeasonEndShopRow>(detail?.SHOP_LIST).reduce<
    Record<string, SerpSeasonEndShopRow[]>
  >((result, item) => {
    const key = String(item.SHOP_CD);
    (result[key] ??= []).push(item);
    return result;
  }, {});
  return Object.keys(grouped).map((key) => ({
    shopName: formatSerpText(grouped[key][0].SHOP_ABBR_NM),
    rows: grouped[key],
  }));
};

const NO_ESCAPE = { interpolation: { escapeValue: false } };

export const buildSerpSeasonEndSubject = (
  source: SerpSeasonEndSource,
  t: TFunction,
): string =>
  t('{{brand}}({{season}}) 시즌 정산의 건', {
    brand: formatSerpText(source.BRAND_EN_NM),
    season: formatSerpText(source.DL_SSN_CD),
    ...NO_ESCAPE,
  });

export const buildSerpSeasonEndModalTitle = (
  source: SerpSeasonEndSource,
  t: TFunction,
): string =>
  t('{{brand}}({{season}}) 정산내역', {
    brand: formatSerpText(source.BRAND_EN_NM),
    season: formatSerpText(source.DL_SSN_CD),
    ...NO_ESCAPE,
  });

export const checkSerpSeasonEndBeforeSave = (
  state: SerpSeasonEndState | null | undefined,
): string | null => {
  if (!state?.source) return SERP_NOT_LOADED_MESSAGE;
  return state.detailSettled ? null : SERP_SEASON_END_LOADING_MESSAGE;
};

/** data 는 본문 원문 + 정산내역 SHOP_LIST(레거시는 정산내역 응답만 남음) */
export const buildSerpSeasonEndAttributes = (
  state: SerpSeasonEndState | null | undefined,
  content: string,
): ApprovalAttributes | null =>
  state?.source
    ? {
        typeCode: state.typeCode,
        sequence: state.sequence,
        content,
        data: state.detail
          ? { ...state.source, SHOP_LIST: state.detail.SHOP_LIST }
          : state.source,
      }
    : null;
